/**
 * 获取哈希路由页面的根路径（核心工具函数）
 * @param {boolean} withBasePath - 是否包含项目基础路径（如 /admin/），默认 true
 * @returns {string} 根路径（如 http://localhost:8080/ 或 http://localhost:8080/admin/）
 */
export function getHashPageRootPath(withBasePath = true) {
  const { protocol, host, pathname } = window.location;
  
  // 1. 基础根路径（协议 + 域名+端口）：如 http://localhost:8080/
  const domainRoot = `${protocol}//${host}/`;

  if (!withBasePath) {
    return domainRoot;
  }

  // 2. 解析项目基础路径（pathname 是 # 前的路径，如 /admin/）
  // 处理 pathname 为空/仅斜杠的情况
  const basePath = pathname === '/' || pathname === '' 
    ? '' 
    : pathname.endsWith('/') ? pathname : `${pathname}/`;

  // 3. 带基础路径的根路径：如 http://localhost:8080/admin/
  return `${protocol}//${host}${basePath}`;
}

/**
 * 获取哈希路由的完整基础路径（根路径 + 哈希路由前缀）
 * 示例：当前 URL 是 http://localhost:8080/admin/#/selfMadeForm?ticket=xxx
 * 返回：http://localhost:8080/admin/#/
 */
export function getHashFullRootPath() {
  const rootPath = getHashPageRootPath();
  // 提取哈希路由的根（#/），排除具体页面和参数
  const hashRoot = window.location.hash.split('?')[0].split('/').slice(0, 2).join('/') || '#/';
  return `${rootPath}${hashRoot}`;
}

/**
 * 示例：解析完整 URL 各部分（便于理解）
 * 当前 URL：http://localhost:8080/admin/#/selfMadeForm?ticket=xxx
 * 解析结果：
 * - 域名根路径：http://localhost:8080/
 * - 带基础路径的根路径：http://localhost:8080/admin/
 * - 哈希完整根路径：http://localhost:8080/admin/#/
 */
export function parseHashUrlParts() {
  const domainRoot = getHashPageRootPath(false);
  const baseRoot = getHashPageRootPath(true);
  const hashRoot = getHashFullRootPath();
  const fullHash = window.location.hash; // #/selfMadeForm?ticket=xxx
  const hashPath = fullHash.split('?')[0]; // #/selfMadeForm
  const hashParams = fullHash.split('?')[1] || ''; // ticket=xxx

  return {
    domainRoot,    // 域名根路径
    baseRoot,      // 带项目基础路径的根路径
    hashRoot,      // 哈希完整根路径
    fullHash,      // 完整哈希部分
    hashPath,      // 哈希路由路径（无参数）
    hashParams     // 哈希路由参数（无?）
  };
}

/**
 * 清理哈希路由中的指定参数（核心函数）
 * @param {Array<string>} paramsToRemove - 要清除的参数名列表（如 ['ticket', 'token']）
 * @returns {void}
 */
export function removeHashParams(paramsToRemove) {
  // 1. 获取完整哈希字符串（如 #/selfMadeForm?ticket=xxx&name=test）
  const fullHash = window.location.hash || '';
  if (!fullHash) return;

  // 2. 拆分哈希路径和哈希参数（以?分割）
  const [hashPath, hashSearch = ''] = fullHash.split('?');
  if (!hashSearch) return; // 无参数则直接返回

  // 3. 解析哈希参数
  const hashParams = new URLSearchParams(hashSearch);
  let hasChanged = false;

  // 4. 移除指定参数
  paramsToRemove.forEach(param => {
    if (hashParams.has(param)) {
      hashParams.delete(param);
      hasChanged = true;
    }
  });

  // 5. 无参数需要移除则返回
  if (!hasChanged) return;

  // 6. 重新拼接哈希字符串
  const newHashSearch = hashParams.toString();
  const newHash = newHashSearch 
    ? `${hashPath}?${newHashSearch}` 
    : hashPath; // 无剩余参数则仅保留路径

  // 7. 替换 URL（无刷新，不修改历史记录）
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}${window.location.search}${newHash}`
  );
}

/**
 * 清理 URL 中（#前 + #后）的指定参数（兼容所有场景）
 * @param {Array<string>} paramsToRemove - 要清除的参数名列表
 */
export function removeAllUrlParams(paramsToRemove) {
  // 第一步：清理 # 前的 URL 参数（如 ?ticket=xxx#/path）
  const urlParams = new URLSearchParams(window.location.search);
  let urlHasChanged = false;

  paramsToRemove.forEach(param => {
    if (urlParams.has(param)) {
      urlParams.delete(param);
      urlHasChanged = true;
    }
  });

  // 拼接新的 search 字符串
  const newSearch = urlParams.toString() ? `?${urlParams.toString()}` : '';

  // 第二步：清理 # 后的哈希参数
  removeHashParams(paramsToRemove);

  // 第三步：如果 # 前参数有变化，更新 URL
  if (urlHasChanged) {
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${newSearch}${window.location.hash}`
    );
  }
}

/**
 * 快捷函数：仅清除 ticket 参数（SSO 场景专用）
 */
export function removeTicketParam() {
  removeAllUrlParams(['ticket']);
}

/**
 * 快捷函数：清除 ticket + token 参数（完整登录态清理）
 */
export function removeAuthParams() {
  removeAllUrlParams(['ticket', 'token', 'redirect']);
}

export function getHashParam(key) {
  // 1. 获取#后的完整哈希字符串（如 /selfMadeForm?ticket=xxx）
  const hashStr = window.location.hash.slice(1); // 去掉#
  if (!hashStr) return null;

  // 2. 拆分路径和参数（以?分割）
  const [, hashSearch = ""] = hashStr.split("?");
  if (!hashSearch) return null;

  // 3. 解析参数
  const hashParams = new URLSearchParams(hashSearch);
  return hashParams.get(key);
}

export function getSsoLoginUrl(){
  const ssoRedirect = encodeURIComponent(getHashPageRootPath(false));
	const ssoLoginUrl = `${process.env.VUE_APP_SSO_URL}?redirect=${ssoRedirect}#/selectOrg`;
  return ssoLoginUrl
}
